import {createContext, useEffect, useRef, useState} from 'react'
import { obterExpiracaoTokenMs, sessaoValida } from '../utils/session'


const authDefault = {
    isAuthenticated: false,
    dataUser: null
}


export const AuthContext = createContext({
    authInfo: authDefault,
    setDataUser: (dataUser) => {
    },
    loading: false,
    notiMessage: '',
    setNotiMessage: () => {}
})

const AuthContextProvider = ({children}) => {
    const [authInfo, setAuthInfo] = useState(authDefault);
    const [loading, setLoading] = useState(true);
    const [notiMessage, setNotiMessage] = useState(null)
    const logoutTimerRef = useRef(null)

    const limparTimerSessao = () => {
        if (logoutTimerRef.current) {
            window.clearTimeout(logoutTimerRef.current)
            logoutTimerRef.current = null
        }
    }

    const encerrarSessaoLocal = () => {
        limparTimerSessao()
        localStorage.removeItem("dataUser")
        setAuthInfo({
            isAuthenticated: false,
            dataUser: null
        })
    }

    const agendarFimDaSessao = (dataUser) => {
        limparTimerSessao()

        const expiraEm = obterExpiracaoTokenMs(dataUser?.token)
        if (!expiraEm) {
            encerrarSessaoLocal()
            return
        }

        const restante = expiraEm - Date.now()
        if (restante <= 0) {
            encerrarSessaoLocal()
            return
        }

        // setTimeout em navegadores tem limite; revalida ao acordar/voltar ao app.
        const atraso = Math.min(restante + 50, 2147483647)
        logoutTimerRef.current = window.setTimeout(() => {
            const salvo = localStorage.getItem("dataUser")
            try {
                const atual = salvo ? JSON.parse(salvo) : null
                if (!sessaoValida(atual)) {
                    encerrarSessaoLocal()
                } else {
                    agendarFimDaSessao(atual)
                }
            } catch (_) {
                encerrarSessaoLocal()
            }
        }, atraso)
    }

    const validarSessaoSalva = () => {
        try {
            const dataUserParse = localStorage.getItem("dataUser")
            const dataUserInitial = dataUserParse ? JSON.parse(dataUserParse) : null

            if (sessaoValida(dataUserInitial)) {
                setAuthInfo({
                    isAuthenticated: true,
                    dataUser: dataUserInitial
                })
                agendarFimDaSessao(dataUserInitial)
                return true
            }

            encerrarSessaoLocal()
            return false
        } catch (_) {
            encerrarSessaoLocal()
            return false
        }
    }

    useEffect(() => {
        // Antes de liberar qualquer rota privada, valida a expiração do JWT.
        // Assim o app não mostra o painel para só depois descobrir que a sessão acabou.
        validarSessaoSalva()
        setLoading(false)

        const revalidarAoVoltar = () => {
            if (document.visibilityState && document.visibilityState !== "visible") return
            validarSessaoSalva()
        }

        window.addEventListener("focus", revalidarAoVoltar)
        window.addEventListener("pageshow", revalidarAoVoltar)
        document.addEventListener("visibilitychange", revalidarAoVoltar)

        return () => {
            limparTimerSessao()
            window.removeEventListener("focus", revalidarAoVoltar)
            window.removeEventListener("pageshow", revalidarAoVoltar)
            document.removeEventListener("visibilitychange", revalidarAoVoltar)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setDataUser = (dataUser) => {
        if (dataUser) {
            // Só grava como autenticado se o token recebido ainda for válido.
            if (!sessaoValida(dataUser)) {
                encerrarSessaoLocal()
                return
            }

            localStorage.setItem("dataUser", JSON.stringify(dataUser))
            setAuthInfo({
                isAuthenticated: true,
                dataUser: dataUser
            })
            agendarFimDaSessao(dataUser)
        } else {
            encerrarSessaoLocal()
        }
    }

    const authContextData = {
        authInfo,
        loading,
        setDataUser,
        notiMessage,
        setNotiMessage,
    }

    return (
        <AuthContext.Provider value={authContextData}>
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContextProvider
